import { Fragment } from "react";
import { Pencil, Trash2, Check, X, Info, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import SearchableSelect from "@/components/SearchableSelect";
import DateField from "@/components/DateField";
import { fmtDate, formatIDR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import api, { formatDetail } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Tabel entries income ledger + footer totals.
 * Extracted dari IncomeLedgerDialog untuk maintainability (session 149-p).
 * Read-only mode diaktifkan via `readOnly` prop; edit + delete disembunyikan.
 */
export default function LedgerEntriesTable({
  entries,
  project,
  retType,
  voAggregation,
  accounts,
  readOnly,
  editing,
  setEditing,
  startEdit,
  cancelEdit,
  saveEdit,
  askDel,
}) {
  const isExempt = !!project?.is_ppn_exempt;
  const voAgg = voAggregation !== undefined ? voAggregation : (project?.vo_aggregation || "separate");
  const { user } = useAuth();
  const isBodLv1 = user?.division === "BOD" && user?.level === 1;
  const [syncing, setSyncing] = useState(false);
  // 2026-06-11: expandable rincian per komponen untuk entry billing gabungan
  // (component_breakdown). Key = entry id.
  const [expandedBreakdown, setExpandedBreakdown] = useState({});
  const toggleBreakdown = (id) => setExpandedBreakdown((s) => ({ ...s, [id]: !s[id] }));

  const runHistoricalSync = async () => {
    if (!window.confirm(
      "Sync Retention Historical:\n\nAksi ini menyamakan nilai `retention_amount` di DB " +
      "dgn formula baru untuk SEMUA project bermode INSIDE + combined VO.\n\n" +
      "Nilai retensi lama (r/100) akan di-recompute jadi r/(100-r) sesuai spec 149-ac.\n" +
      "Idempotent — kalau sudah sinkron, tidak ada perubahan.\n\nLanjut?"
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-combined-retention");
      toast.success(
        `✅ Sync selesai: ${data.updated_entries} entry di-update dari ${data.scanned_entries} di-scan ` +
        `(${data.projects_touched} project, ${data.combined_parents_found} combined parent).`
      );
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Sync gagal");
    } finally { setSyncing(false); }
  };

  return (
    <div className="border border-slate-200">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            {["Invoice Date", "Received Date", "Invoice", "Income Tax"].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}
            {/* Retention header — dilengkapi tooltip formula + tombol admin sync (149-ac). */}
            <th className="px-3 py-2 overline text-slate-600">
              <div className="flex items-center gap-1">
                <span>Retention</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="retention-info-popover-trigger"
                      className="text-slate-400 hover:text-[#0B1D3A] transition-colors"
                      title="Formula retensi"
                    ><Info size={11} /></button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 p-3 rounded-none border border-slate-300 text-[10px] leading-relaxed text-slate-700" data-testid="retention-info-popover">
                    <div className="font-semibold text-[#0B1D3A] mb-1.5 uppercase tracking-wider text-[9px]">Formula Retensi per Kombinasi</div>
                    <table className="w-full border-collapse text-[10px]">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-200">
                          <th className="text-left py-1 font-semibold">Jenis + VO</th>
                          <th className="text-left py-1 font-semibold">Formula</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-slate-100">
                          <td className="py-1 pr-2 text-slate-600">Outside · any</td>
                          <td className="py-1 tabular-nums">0 (di-claim terpisah)</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-1 pr-2 text-slate-600">Inside · <b>Terpisah</b></td>
                          <td className="py-1 tabular-nums">amount × pct / 100</td>
                        </tr>
                        <tr>
                          <td className="py-1 pr-2 text-slate-600">Inside · <b>Digabung</b></td>
                          <td className="py-1 tabular-nums font-semibold text-[#0B1D3A]">amount × pct / (100 − pct)</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-2 text-[9px] text-slate-500 italic">
                      Pada VO <b>Digabung</b>, nilai tagihan sudah 90% dari bruto pekerjaan (net-of-retention). Formula gross-up mengembalikan 10% retensi sesuai kontrak.
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-200 text-[9px] text-slate-500">
                      Project ini: <b className="text-[#0B1D3A]">{voAgg === "combined" ? "VO Digabung" : "VO Terpisah"}</b>
                      {" · "}
                      <b className="text-[#0B1D3A]">{(retType === "outside") ? "Retensi Outside" : "Retensi Inside"}</b>
                    </div>
                    {isBodLv1 && (
                      <button
                        type="button"
                        onClick={runHistoricalSync}
                        disabled={syncing}
                        data-testid="retention-sync-historical-btn"
                        className="mt-3 w-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold border border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white transition-colors py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <RefreshCw size={11} className={syncing ? "animate-spin" : ""} />
                        {syncing ? "Menyinkronkan…" : "Sync Retention Historical (BOD)"}
                      </button>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </th>
            {["Net Received", "Bank", "Account", "Note", ""].map((h) => <th key={h} className="px-3 py-2 overline text-slate-600">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && <tr><td colSpan={9} className="px-3 py-4 text-center text-slate-400">No income entries yet.</td></tr>}
          {entries.map((e) => {
            const isEd = editing?.id === e.id;
            if (isEd) {
              return (
                <tr key={e.id} className="border-b border-slate-100 bg-emerald-50/40">
                  <td className="px-3 py-2 min-w-[140px]">
                    <DateField testid={`income-edit-invoice-date-${e.id}`} value={editing.invoice_date || ""} onChange={(v) => setEditing({ ...editing, invoice_date: v })} />
                  </td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <div className="space-y-1">
                      <label className="flex items-center gap-1 text-[9px] cursor-pointer select-none" data-testid={`income-edit-waiting-toggle-${e.id}`}>
                        <input
                          type="checkbox"
                          checked={!!editing.waiting_payment}
                          onChange={(ev) => setEditing({ ...editing, waiting_payment: ev.target.checked, date: ev.target.checked ? "" : (editing.date || new Date().toISOString().slice(0, 10)) })}
                          className="h-3 w-3 accent-amber-600"
                          data-testid={`income-edit-waiting-checkbox-${e.id}`}
                        />
                        <span className="uppercase tracking-wider font-semibold text-amber-700">Waiting</span>
                      </label>
                      {editing.waiting_payment ? (
                        <div className="h-8 border border-amber-300 bg-amber-50 px-2 flex items-center text-[10px] text-amber-800 italic" data-testid={`income-edit-waiting-badge-${e.id}`}>⏳ Waiting Payment</div>
                      ) : (
                        <DateField testid={`income-edit-date-${e.id}`} value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <Input data-testid={`income-edit-amount-${e.id}`} type="number" min="0" step="1" value={editing.amount} onChange={(ev) => setEditing({ ...editing, amount: ev.target.value })} className="rounded-none h-8 text-xs tabular-nums" />
                  </td>
                  <td className="px-3 py-2 tabular-nums text-sky-700 text-[11px] italic">auto</td>
                  <td className="px-3 py-2 tabular-nums text-amber-700 text-[11px] italic">auto</td>
                  <td className="px-3 py-2 tabular-nums text-emerald-700 text-[11px] italic">auto</td>
                  <td className="px-3 py-2 min-w-[220px]" colSpan={2}>
                    {/* 2026-10-09 (user request): rekening bank searchable di edit row. */}
                    <SearchableSelect
                      testid={`income-edit-account-${e.id}`}
                      value={editing.account_id}
                      onChange={(v) => setEditing({ ...editing, account_id: v })}
                      options={accounts.map((a) => ({ value: a.id, label: `${a.bank_name} · ${a.account_no}` }))}
                      placeholder="Bank account"
                      searchPlaceholder="Ketik nama bank…"
                      emptyText="Rekening tidak ditemukan"
                    />
                  </td>
                  <td className="px-3 py-2 min-w-[200px]">
                    <Input data-testid={`income-edit-note-${e.id}`} value={editing.note} onChange={(ev) => setEditing({ ...editing, note: ev.target.value })} className="rounded-none h-8 text-xs" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button data-testid={`income-save-${e.id}`} onClick={saveEdit} className="text-emerald-600 hover:text-emerald-800 p-1"><Check size={13} /></button>
                      <button data-testid={`income-cancel-${e.id}`} onClick={cancelEdit} className="text-slate-500 hover:text-slate-700 p-1"><X size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            }
            const dpp = Number(e.amount) || 0;
            const ppn = isExempt ? 0 : Math.round(dpp * 0.11);
            const bruto = dpp + ppn;
            const p = e.pph_amount != null ? Number(e.pph_amount) : 0;
            const et = e.retention_type || retType;
            const ePct = e.retention_pct != null ? Number(e.retention_pct) : (Number(project?.retention_pct) || 0);
            const hasReceived = !!e.date && !e.waiting_payment;
            // Retensi per-row (149-ae):
            //  - claim entry / no pct → 0
            //  - combined VO: gross-up dpp × pct / (100-pct), TAPI hanya untuk received invoice
            //  - separate VO + outside: 0 (di-claim terpisah)
            //  - separate VO + inside: dpp × pct / 100
            let r;
            if (e.is_retention_claim || ePct <= 0 || ePct >= 100) {
              r = 0;
            } else if (voAgg === "combined") {
              r = hasReceived ? Math.round(dpp * ePct / (100 - ePct)) : 0;
            } else if (et === "outside") {
              r = 0;
            } else {
              r = Math.round(dpp * ePct / 100);
            }
            return (
              <Fragment key={e.id}>
              <tr className={`border-b border-slate-100 ${e.is_retention_claim ? "bg-amber-50/60" : ""}`}>
                <td className="px-3 py-2 tabular-nums text-slate-600">{e.invoice_date ? fmtDate(e.invoice_date) : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 tabular-nums">{e.date ? fmtDate(e.date) : (
                  <span className="text-[10px] px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 font-semibold uppercase tracking-wider" data-testid={`income-waiting-${e.id}`}>⏳ Waiting</span>
                )}</td>
                <td className="px-3 py-2 tabular-nums font-semibold text-[#0B1D3A]">
                  <div>{formatIDR(bruto)}
                    {e.is_retention_claim && (
                      <span className="ml-1 text-[9px] px-1 py-0.5 uppercase tracking-wider font-semibold border border-amber-300 bg-amber-100 text-amber-800 align-middle">Claim</span>
                    )}
                  </div>
                  {!isExempt && dpp > 0 && (
                    <div className="mt-0.5 text-[9px] leading-tight text-slate-500" data-testid={`income-ppn-${e.id}`}>
                      <span className="text-slate-500">DPP <span className="tabular-nums">{formatIDR(dpp)}</span></span>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-sky-700">PPN 11% <span className="tabular-nums">{formatIDR(ppn)}</span></span>
                    </div>
                  )}
                  {isExempt && (
                    <div className="mt-0.5 text-[9px] italic text-slate-400" data-testid={`income-ppn-exempt-${e.id}`}>Non-PPN</div>
                  )}
                  {/* 2026-06-11: badge expandable untuk billing gabungan (VO Digabung).
                      Klik untuk melihat rincian nominal per komponen VO/WO. */}
                  {Array.isArray(e.component_breakdown) && e.component_breakdown.length > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleBreakdown(e.id)}
                      data-testid={`income-breakdown-toggle-${e.id}`}
                      className="mt-0.5 inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-semibold border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                    >
                      {expandedBreakdown[e.id] ? "▴ Tutup Rincian" : `▾ Rincian ${e.component_breakdown.length} Komponen`}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums text-sky-700">
                  {e.pph_amount != null && Number(e.pph_amount) > 0 ? (
                    <>
                      {formatIDR(e.pph_amount)}
                      {e.pph_pct != null && <span className="text-[10px] text-sky-500 ml-1">({e.pph_pct}%)</span>}
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 tabular-nums text-amber-700">
                  {r > 0 ? (
                    <>
                      {formatIDR(r)}
                      {ePct > 0 && <span className="text-[10px] text-amber-500 ml-1">({ePct}%)</span>}
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 tabular-nums font-semibold text-emerald-700">
                  {formatIDR(bruto - p - r)}
                </td>
                <td className="px-3 py-2">{e.bank_name}</td>
                <td className="px-3 py-2 font-mono">{e.account_no}</td>
                <td className="px-3 py-2 text-slate-600" title={`Recorded by ${e.recorded_by_name || ""}`}>{e.note || "-"}</td>
                <td className="px-3 py-2 text-right">
                  {readOnly ? (
                    <span className="text-[10px] text-slate-400 italic">view only</span>
                  ) : (
                    <div className="inline-flex gap-1">
                      <button data-testid={`income-edit-${e.id}`} onClick={() => startEdit(e)} className="text-slate-500 hover:text-[#0B1D3A] p-1" title="Edit"><Pencil size={12} /></button>
                      <button data-testid={`income-del-${e.id}`} onClick={() => askDel(e.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete"><Trash2 size={13} /></button>
                    </div>
                  )}
                </td>
              </tr>
              {/* 2026-06-11: baris detail expandable — rincian nominal per komponen
                  untuk entry billing gabungan (component_breakdown). */}
              {expandedBreakdown[e.id] && Array.isArray(e.component_breakdown) && e.component_breakdown.length > 0 && (
                <tr className="border-b border-indigo-100 bg-indigo-50/40" data-testid={`income-breakdown-${e.id}`}>
                  <td colSpan={9} className="px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700 mb-1">Rincian Billing Gabungan</div>
                    <table className="w-full text-[10px]">
                      <tbody>
                        {e.component_breakdown.map((b, bi) => (
                          <tr key={bi} data-testid={`income-breakdown-row-${e.id}-${bi}`} className="border-b border-indigo-100 last:border-b-0">
                            <td className="py-1 pr-2 text-slate-700 font-medium">{b.project_name || "-"}</td>
                            <td className="py-1 pr-2 tabular-nums text-[#0B1D3A] font-semibold whitespace-nowrap">{formatIDR(b.amount)}</td>
                            <td className="py-1 text-slate-500">{b.note || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
        {entries.length > 0 && (() => {
          const totalDpp = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
          const totalPpn = isExempt ? 0 : Math.round(totalDpp * 0.11);
          const totalBruto = totalDpp + totalPpn;
          const totalPph = entries.reduce((s, e) => s + (e.pph_amount != null ? Number(e.pph_amount) : 0), 0);
          // Total Retensi (149-af fix): SAMA persis dengan formula per-row supaya
          // footer = jumlah kolom Retention di atas. Sebelumnya masih pakai pct/100
          // (149-aa) yang bikin footer beda dgn per-row untuk mode combined VO.
          const projRetPct = Number(project?.retention_pct) || 0;
          const totalRet = entries.reduce((s, e) => {
            if (e.is_retention_claim) return s;
            const ePct = e.retention_pct != null ? Number(e.retention_pct) : projRetPct;
            if (ePct <= 0 || ePct >= 100) return s;
            const amt = Number(e.amount) || 0;
            const et = e.retention_type || retType;
            const hasReceived = !!e.date && !e.waiting_payment;
            if (voAgg === "combined") {
              return s + (hasReceived ? Math.round(amt * ePct / (100 - ePct)) : 0);
            }
            if (et === "outside") return s;
            return s + Math.round(amt * ePct / 100);
          }, 0);
          const totalNet = totalBruto - totalPph - totalRet;
          return (
            <tfoot data-testid="income-footer-totals" className="border-t-2 border-slate-300 bg-slate-50">
              <tr className="text-[11px]">
                <td colSpan={2} className="px-3 py-2 overline text-slate-600 font-semibold text-right">Total ({entries.length} entri):</td>
                <td className="px-3 py-2 tabular-nums font-bold text-[#0B1D3A]" data-testid="income-total-dpp">
                  {formatIDR(totalBruto)}
                  <div className="text-[9px] font-normal text-slate-500 leading-tight">
                    {isExempt ? (
                      <span className="italic">Non-PPN</span>
                    ) : (
                      <>DPP <span className="tabular-nums" data-testid="income-total-dpp-only">{formatIDR(totalDpp)}</span> · PPN 11% <span className="tabular-nums text-sky-700" data-testid="income-total-ppn">{formatIDR(totalPpn)}</span></>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 tabular-nums text-sky-700 font-semibold" data-testid="income-total-pph">
                  {totalPph > 0 ? formatIDR(totalPph) : <span className="text-slate-300">—</span>}
                  <div className="text-[9px] font-normal text-slate-500">PPh</div>
                </td>
                <td className="px-3 py-2 tabular-nums text-amber-700 font-semibold" data-testid="income-total-retention">
                  {totalRet > 0 ? formatIDR(totalRet) : <span className="text-slate-300">—</span>}
                  <div className="text-[9px] font-normal text-slate-500">Retensi</div>
                </td>
                <td className="px-3 py-2 tabular-nums font-bold text-emerald-700" data-testid="income-total-net">
                  {formatIDR(totalNet)}
                  <div className="text-[9px] font-normal text-slate-500">Net Diterima (Bruto − PPh − Retensi)</div>
                </td>
                <td colSpan={4} className="px-3 py-2 text-right">
                  <div className="inline-flex flex-col items-end gap-0.5">
                    {!isExempt ? (
                      <div className="text-emerald-800 font-bold"><span className="overline">Bruto (DPP+PPN)</span> <span className="tabular-nums" data-testid="income-total-bruto">{formatIDR(totalBruto)}</span></div>
                    ) : (
                      <div className="text-slate-500 italic text-[10px]">Project Non-PPN — Bruto = DPP</div>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          );
        })()}
      </table>
    </div>
  );
}
